import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import { settingsHistory, settingsSections, toDeployEnv, NO_SETTING_ACCESS } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { usePageTab } from '../../shell/Shell';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { AskDialog } from './AskDialog';
import { I, PageCtx, pre, sx, useApi, type AskSpec, type SettingsPageCtx } from './kit';
import { KEYWORDS, SECTION_KEY, SECTION_SLUG, groupOf, groupsFor, lensOf, sectionsForRole, type SectionName } from './lens';
import { histRow } from './history';
import { SECTIONS } from './sections';
import './settings.css';

/**
 * Settings (design T8624, settingsVals L21734, seGuard L21679). A searchable, grouped
 * section list on the left; the section on the right under a bar that names its group,
 * says who changed it last and opens its history. Each role sees the design's own list
 * for it (the server enforces the same matrix, v3/settings/sections.ts); Danger Zone is
 * the Super Admin's alone. Every destructive action asks first and sends confirm: true.
 */
export function Settings() {
  const { role } = useConsoleRole();
  const { session } = useSession();
  const roReason = useReadOnlyReason();
  const lens = useMemo(() => lensOf(role), [role]);
  // Where the server runs decides the environment-only tabs (settings-access.ts): until it
  // answers, production, so a Developer never sees Atlas Assistant in production.
  const access = useApi((c, signal) => settingsSections(c, { signal }), []);
  const env = toDeployEnv(access.data?.env);
  const SEC = useMemo(() => sectionsForRole(role, env), [role, env]);
  const [params, setParams] = useSearchParams();
  // A direct link to a tab this role may not open says so instead of showing another tab.
  const urlNamed = (Object.keys(KEYWORDS) as SectionName[]).find((n) => SECTION_SLUG(n) === params.get('section')) ?? null;
  const fromUrl = [...SEC, 'Danger Zone' as SectionName].find((n) => SECTION_SLUG(n) === params.get('section'));
  const seeDangerZone = lens.lens === 'Operator' && lens.superAdmin;
  const settled = !!access.data || !!access.error;
  const denied = settled && urlNamed && !SEC.includes(urlNamed) && !(urlNamed === 'Danger Zone' && seeDangerZone) ? urlNamed : null;
  const [secState, setSec] = useState<SectionName>(fromUrl && (fromUrl !== 'Danger Zone' || seeDangerZone) ? fromUrl : SEC[0]);
  const sec: SectionName = SEC.includes(secState) || (secState === 'Danger Zone' && seeDangerZone) ? secState : SEC[0];
  usePageTab(sec);
  // A tab that opens only once the environment is known (a Developer outside production).
  useEffect(() => {
    if (urlNamed && SEC.includes(urlNamed)) setSec(urlNamed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SEC]);
  const [q, setQ] = useState('');
  const [histOpen, setHistOpen] = useState(false);
  const [ask, setAsk] = useState<AskSpec | null>(null);
  const [savedAt, setSavedAt] = useState<{ t: string; undo: () => Promise<void>; until: number } | null>(null);
  const [, force] = useState(0);
  const timer = useRef<number | null>(null);

  const pick = (n: SectionName) => {
    setSec(n);
    const next = new URLSearchParams(params);
    if (n === SEC[0]) next.delete('section');
    else next.set('section', SECTION_SLUG(n));
    setParams(next, { replace: true });
  };

  // ---- the section's change history (10a) ----
  const key = SECTION_KEY[sec];
  const histKey = key ?? null;
  const hist = useApi((c, signal) => settingsHistory(c, { query: { section: histKey!, limit: 50 }, signal }), [histKey], !!histKey);
  const rows = (hist.data?.changes ?? []).map((c) => histRow(c, session?.user.id ?? null));
  const changed = hist.reload;

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  const saved = useCallback((s: { t: string; undo: () => Promise<void> }) => {
    setSavedAt({ ...s, until: Date.now() + 10_000 });
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => force((x) => x + 1), 10_100);
  }, []);

  const ctx: SettingsPageCtx = {
    lens,
    facilityId: session?.user.organizationId ?? null,
    sec,
    roReason,
    ask: setAsk,
    changed,
    saved,
  };

  // ---- the section list (L22051) ----
  const qq = q.trim().toLowerCase();
  const hits = qq
    ? SEC.map((n): [SectionName, string] | null => {
        const hay = (n + ' ' + (KEYWORDS[n] || '')).toLowerCase();
        if (hay.indexOf(qq) < 0) return null;
        if (n.toLowerCase().indexOf(qq) >= 0) return [n, ''];
        const w = (KEYWORDS[n] || '').split(' ').filter((x) => x.indexOf(qq) >= 0 || qq.indexOf(x) >= 0)[0] || qq;
        return [n, w];
      }).filter((h): h is [SectionName, string] => h !== null)
    : null;
  type Item = { isHead: true; name: string } | { isHead: false; name: SectionName; hit: string };
  const list: Item[] = [];
  if (hits) hits.forEach((h) => list.push({ isHead: false, name: h[0], hit: h[1] }));
  else {
    const shown = new Set<string>();
    groupsFor(lens.lens).forEach(([g, names]) => {
      const ns = names.filter((n) => SEC.includes(n));
      if (!ns.length) return;
      list.push({ isHead: true, name: g });
      ns.forEach((n) => {
        shown.add(n);
        list.push({ isHead: false, name: n, hit: '' });
      });
    });
    const rest = SEC.filter((n) => !shown.has(n));
    if (rest.length) {
      if (list.length) list.push({ isHead: true, name: 'You' });
      rest.forEach((n) => list.push({ isHead: false, name: n, hit: '' }));
    }
  }

  const showBar = !denied && !['Sales', 'Dev', 'Family', 'Aithora Home'].includes(lens.lens);
  const secHist = histKey ? rows : [];
  const savedNow = savedAt && savedAt.until > Date.now() && sec === 'Organization' ? savedAt : null;
  const last = hist.error && histKey
    ? 'History could not be loaded'
    : secHist.length
      ? 'Last change ' + secHist[0].at + ' by ' + secHist[0].who + (secHist[0].tag ? ' (' + secHist[0].tag + ')' : '')
      : 'No changes recorded yet';
  const Section: ComponentType = SECTIONS[sec];

  return (
    <PageCtx.Provider value={ctx}>
      <div className="v9type" data-screen-label="Settings" data-testid="settings-page" style={sx('display:grid;grid-template-columns:210px minmax(0,1fr);gap:18px;align-items:stretch;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;')}>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search settings" aria-label="Search settings" style={sx('flex:0 0 auto;width:100%;box-sizing:border-box;height:34px;padding:0 11px;margin-bottom:8px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FCFAF4;font-size:16px;color:#22272E;font-family:inherit;outline:none;')} />
          <nav aria-label="Settings sections" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;display:flex;flex-direction:column;gap:1px;padding-right:2px;')}>
            {list.map((x, i) => (
              <Fragment key={i}>
                {x.isHead ? (
                  <div style={sx('font-size:14px;letter-spacing:.12em;text-transform:uppercase;color:#54606C;font-weight:700;padding:9px 11px 3px;white-space:nowrap;')}>{I(x.name)}</div>
                ) : (
                  <>
                    <a
                      href="#"
                      aria-current={x.name === sec ? 'page' : undefined}
                      onClick={(e) => {
                        pre(e);
                        pick(x.name);
                      }}
                      style={sx(
                        'display:flex;align-items:center;gap:10px;flex-wrap:nowrap;white-space:nowrap;padding:7px 11px;border-radius:8px;font-size:16px;font-weight:' +
                          (x.name === sec ? '600' : '500') + ';text-decoration:none;' + (x.name === sec ? 'color:#2F5C97;background:rgba(79,138,214,.12);' : 'color:#3C454F;'),
                      )}
                    >
                      <span style={sx('width:6px;height:6px;border-radius:2px;flex:0 0 6px;background:' + (x.name === sec ? '#2F5C97' : 'rgba(34,39,46,.22)') + ';')} />
                      {I(x.name)}
                      <span style={sx('flex:1;')} />
                      {x.name === sec && <span style={sx('width:6px;height:6px;border-radius:50%;background:#2F5C97;flex:0 0 6px;')} />}
                    </a>
                    {x.hit && (
                      <span style={sx('display:block;font-size:16px;color:#54606C;padding:0 11px 5px 27px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>
                        {'matches “'}
                        {I(x.hit)}
                        {'”'}
                      </span>
                    )}
                  </>
                )}
              </Fragment>
            ))}
            {hits && hits.length === 0 && <span style={sx('font-size:16px;color:#54606C;padding:8px 11px;')}>No setting matches that.</span>}
          </nav>
          {seeDangerZone && (
            <div style={sx('flex:0 0 auto;margin-top:12px;padding-top:10px;border-top:1px solid rgba(34,39,46,.1);')}>
              <a
                href="#"
                className="se-hover-danger"
                aria-current={sec === 'Danger Zone' ? 'page' : undefined}
                onClick={(e) => {
                  pre(e);
                  pick('Danger Zone');
                }}
                style={sx('display:flex;align-items:center;gap:10px;flex-wrap:nowrap;white-space:nowrap;padding:8px 11px;border-radius:8px;font-size:16px;color:#B3261E;text-decoration:none;')}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={sx('flex:0 0 15px;')} aria-hidden="true">
                  <path d="M12 3.6 2.6 20.4h18.8Z" />
                  <path d="M12 9.6v5" />
                  <path d="M12 17.6h.01" />
                </svg>
                Danger Zone
              </a>
            </div>
          )}
        </div>

        <div data-testid="settings-section" style={sx('min-width:0;min-height:0;overflow-y:auto;overflow-x:hidden;padding-right:3px;')}>
          {showBar && (
            <>
              <div style={sx('position:sticky;top:0;z-index:2;background:#F5F1E8;display:flex;align-items:center;gap:4px 14px;flex-wrap:wrap;min-width:0;padding:2px 0 9px;margin-bottom:10px;border-bottom:1px solid rgba(34,39,46,.10);')}>
                <span style={sx('font-size:14px;letter-spacing:.12em;text-transform:uppercase;color:#2F5C97;font-weight:700;white-space:nowrap;')}>{I(groupOf(sec, lens.lens) + ' · ' + sec)}</span>
                <span data-testid="settings-last" style={sx('font-size:16px;color:#54606C;min-width:0;')}>{I(last)}</span>
                <span style={sx('flex:1 1 10px;')} />
                {savedNow && (
                  <span style={sx('display:inline-flex;align-items:center;gap:10px;font-size:16px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>
                    {'✓ '}
                    {I('Saved ' + savedNow.t + ' by you')}
                    <a
                      href="#"
                      onClick={(e) => {
                        pre(e);
                        const u = savedNow.undo;
                        setSavedAt(null);
                        void u().then(changed);
                      }}
                      style={sx('color:#2F5C97;text-decoration:none;')}
                    >
                      Undo
                    </a>
                  </span>
                )}
                <a
                  href="#"
                  aria-expanded={histOpen}
                  onClick={(e) => {
                    pre(e);
                    setHistOpen(!histOpen);
                  }}
                  style={sx('display:inline-flex;align-items:center;min-height:30px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
                >
                  {I(histOpen ? 'Hide history' : 'History' + (secHist.length ? ' (' + secHist.length + ')' : ''))}
                </a>
              </div>
              {histOpen && (
                <div data-testid="settings-history" style={sx('min-width:0;margin:-4px 0 12px;border-bottom:1px solid rgba(34,39,46,.10);padding-bottom:6px;')}>
                  {secHist.slice(0, 8).map((h, i) => (
                    <div key={i} style={sx('display:grid;grid-template-columns:minmax(0,110px) minmax(0,130px) minmax(0,1fr) auto;gap:4px 12px;align-items:baseline;padding:6px 0;border-top:1px solid rgba(34,39,46,.06);min-width:0;')}>
                      <span style={sx('font-size:16px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{I(h.at)}</span>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(h.who)}</span>
                      <span style={sx('font-size:16px;color:#3C454F;min-width:0;text-wrap:pretty;')}>{I(h.what)}</span>
                      <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;white-space:nowrap;')}>{I(h.tag)}</span>
                    </div>
                  ))}
                  {secHist.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>No changes recorded in this section yet.</div>}
                </div>
              )}
            </>
          )}
          {denied ? (
            <div role="alert" data-testid="settings-no-access" style={sx('font-size:16px;color:#3C454F;padding:18px 4px;')}>{NO_SETTING_ACCESS}</div>
          ) : (
            <Section key={sec} />
          )}
        </div>
      </div>
      {ask && (
        <AskDialog
          sec={ask.sec}
          title={ask.title}
          confirmLabel={ask.btn}
          body={ask.body}
          extra={ask.extra}
          onConfirm={async () => {
            await ask.go();
            changed();
          }}
          onClose={() => setAsk(null)}
        />
      )}
    </PageCtx.Provider>
  );
}
